import { useEffect, useState, useCallback } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * Module-level cache so navigating between pages doesn't refetch.
 * Same pattern as useReports.
 */
let _cache = null;
let _cacheCount = null;

/**
 * Fetches the recent-N transcripts list from `/api/transcripts?count=N`.
 * Returns { transcripts, loading, error, reload }. Items are TranscriptListItem
 * (lightweight; no rawTranscript / chunk text bodies).
 */
export const useTranscripts = (count = 30) => {
  const [transcripts, setTranscripts] = useState(_cache && _cacheCount === count ? _cache : []);
  const [loading, setLoading] = useState(!(_cache && _cacheCount === count));
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall(API_CONFIG.endpoints.transcriptsWithCount(count));
      const list = Array.isArray(data) ? data : [];
      _cache = list;
      _cacheCount = count;
      setTranscripts(list);
    } catch (err) {
      console.error('Failed to load transcripts', err);
      setError('Failed to load transcripts');
    } finally {
      setLoading(false);
    }
  }, [count]);

  useEffect(() => {
    if (_cache && _cacheCount === count) return; // already warm
    load();
  }, [count, load]);

  return { transcripts, loading, error, reload: load };
};

/**
 * Invalidate the module-level cache. Called after a successful upload so
 * the next render re-fetches and picks up the new transcript.
 */
export const invalidateTranscriptsCache = () => {
  _cache = null;
  _cacheCount = null;
};
